/* eslint-disable react-refresh/only-export-components */
import { createFileRoute } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Archive,
  Copy,
  Eye,
  HelpCircle,
  Pencil,
  Plus,
  Star,
} from 'lucide-react';
import { useState } from 'react';
import { fetchAdminAttachmentTemplates } from '@/api/orders';
import type { AdminAttachmentTemplateRow } from '@/api/orders/types';
import { AppLink } from '@/components/shared/AppLink';
import { DataTable } from '@/components/shared/DataTable';
import type { FilterDef, RowAction } from '@/components/shared/DataTable/types';
import { PageContainer } from '@/components/shared/PageContainer';
import { UpdatedAtValue } from '@/components/shared/ValueRenderers';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ATTACHMENT_TEMPLATE_TRACING_TYPES } from '@/lib/constants/attachmentTemplate';
import { queryClient } from '@/lib/utils/queryClient';
import type { AttachmentTemplateVisibility } from '../../-hooks/useAdminAttachmentTemplates';
import { useAttachmentTemplatesListController } from '../../-hooks/useAttachmentTemplatesListController';

export const Route = createFileRoute(
  '/(client)/orders/(pages)/attachment-templates/',
)({
  validateSearch: (search: Record<string, unknown>) => ({
    sort: typeof search.sort === 'string' ? search.sort : undefined,
    visibility:
      typeof search.visibility === 'string' ? search.visibility : undefined,
  }),
  loader: () =>
    queryClient.ensureQueryData({
      queryKey: [
        'orders',
        'adminAttachmentTemplates',
        { page: 1, count: 50, templateType: 'PRODUCT_BACKWARD' },
      ],
      queryFn: () => {
        const qs = new URLSearchParams({
          page: '1',
          count: '50',
          templateType: 'PRODUCT_BACKWARD',
        }).toString();
        return fetchAdminAttachmentTemplates(qs);
      },
    }),
  component: AttachmentTemplatesListPage,
});

function AttachmentTemplatesListPage() {
  const navigate = Route.useNavigate();
  const [archiveTarget, setArchiveTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const {
    t,
    table,
    rows,
    total,
    isLoading,
    visibility,
    setVisibility,
    preferenceMutation,
    cloneMutation,
    archiveMutation,
    handleSetDefault,
    handleClone,
    handleArchive,
  } = useAttachmentTemplatesListController();

  // 'all' added automatically by DataTable QuickFiltersBar
  const quickFilters: { id: AttachmentTemplateVisibility; label: string }[] = [
    { id: 'global', label: t('list.filterGlobal') },
    { id: 'private', label: t('list.filterPrivate') },
    { id: 'shared', label: t('list.filterShared') },
  ];

  const columns: ColumnDef<AdminAttachmentTemplateRow, unknown>[] = [
    {
      id: 'name',
      header: t('list.colName'),
      accessorKey: 'name',
      enableSorting: true,
      cell: ({ row }) => (
        <AppLink
          to='/orders/attachment-templates/$id'
          params={{ id: row.original.id }}
          bold
        >
          {row.original.name}
        </AppLink>
      ),
      size: 280,
    },
    {
      id: 'tracingType',
      header: t('list.colTracingType'),
      accessorKey: 'tracingType',
      enableSorting: false,
      cell: ({ getValue }) => {
        const key = getValue<string>();
        const label = ATTACHMENT_TEMPLATE_TRACING_TYPES[key] ?? key;
        return (
          <span className='inline-flex items-center px-2 py-0.5 rounded-md bg-muted text-xs font-medium'>
            {label}
          </span>
        );
      },
      size: 200,
    },
    {
      id: 'isGlobal',
      header: t('list.colIsGlobal'),
      accessorKey: 'isGlobal',
      enableSorting: false,
      cell: ({ getValue }) =>
        getValue<boolean>() ? (
          <Badge variant='info' className='rounded-md'>
            {t('list.global')}
          </Badge>
        ) : (
          <span className='text-xs text-muted-foreground'>
            {t('list.notGlobal')}
          </span>
        ),
      size: 120,
    },
    {
      id: 'isPreference',
      header: t('list.colDefault'),
      accessorKey: 'isPreference',
      enableSorting: false,
      cell: ({ getValue }) =>
        getValue<boolean>() ? (
          <span className='inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-primary/10 text-primary text-xs font-medium'>
            <Star className='size-3' />
            {t('list.default')}
          </span>
        ) : null,
      size: 140,
    },
    {
      id: 'updatedAt',
      header: t('list.colUpdatedAt'),
      accessorKey: 'updatedAt',
      enableSorting: true,
      cell: ({ row }) => (
        <UpdatedAtValue
          value={row.original.updatedAt}
          userName={row.original.updatedByUserName}
          companyName={row.original.updatedByCompanyName}
        />
      ),
      size: 200,
    },
  ];

  const rowActions: RowAction<AdminAttachmentTemplateRow>[] = [
    {
      label: t('list.actionView'),
      icon: <Eye className='size-3.5' />,
      onClick: row =>
        void navigate({
          to: '/orders/attachment-templates/$id',
          params: { id: row.id },
        }),
    },
    {
      label: t('list.actionEdit'),
      icon: <Pencil className='size-3.5' />,
      onClick: row =>
        void navigate({
          to: '/orders/attachment-templates/$id/edit',
          params: { id: row.id },
        }),
    },
    {
      label: t('list.actionClone'),
      icon: <Copy className='size-3.5' />,
      onClick: async row => {
        const newId = await handleClone(row.id);
        if (newId) {
          void navigate({
            to: '/orders/attachment-templates/$id/edit',
            params: { id: newId },
          });
        }
      },
      loading: row =>
        cloneMutation.isPending && cloneMutation.variables === row.id,
    },
    {
      label: t('list.setDefault'),
      icon: <Star className='size-3.5' />,
      hidden: row => row.isPreference || !!row.owner,
      onClick: row => void handleSetDefault(row.id),
      loading: row =>
        preferenceMutation.isPending && preferenceMutation.variables === row.id,
    },
    {
      label: t('list.actionArchive'),
      icon: <Archive className='size-3.5' />,
      hidden: row => row.isGlobal,
      onClick: row => setArchiveTarget({ id: row.id, name: row.name }),
    },
  ];

  const filterDefs: FilterDef<AdminAttachmentTemplateRow>[] = [
    {
      id: 'name',
      label: t('list.colName'),
      type: 'text',
    },
  ];

  return (
    <PageContainer>
      <DataTable
        data={rows}
        columns={columns}
        loading={isLoading}
        rowActions={rowActions}
        filterDefs={filterDefs}
        rowIdKey='id'
        emptyMessage={t('list.empty')}
        sorting={table.sorting}
        onSortingChange={table.onSortingChange}
        filters={table.filters}
        onFiltersChange={table.onFiltersChange}
        pagination={{ pageIndex: table.page - 1, pageSize: table.limit }}
        onPaginationChange={({ pageIndex, pageSize }) =>
          table.onPaginationChange({ pageIndex, pageSize })
        }
        manualPageCount={Math.ceil(total / table.limit)}
        quickFilters={quickFilters}
        activeQuickFilter={visibility}
        onQuickFilterChange={id =>
          setVisibility(id as AttachmentTemplateVisibility)
        }
        toolbarActions={[
          {
            type: 'custom',
            id: 'create',
            content: (
              <Button
                onClick={() =>
                  void navigate({ to: '/orders/attachment-templates/create' })
                }
              >
                <Plus />
                {t('list.create')}
              </Button>
            ),
          },
          {
            type: 'button',
            id: 'help',
            label: t('list.helpTooltip'),
            icon: <HelpCircle />,
            onClick: () => {},
          },
        ]}
      />

      {/* Archive confirm dialog */}
      <AlertDialog
        open={!!archiveTarget}
        onOpenChange={open => {
          if (!open) setArchiveTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('detail.archiveTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('detail.archiveDescription').replace(
                '{{name}}',
                archiveTarget?.name ?? '',
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setArchiveTarget(null)}
              disabled={archiveMutation.isPending}
            >
              {t('detail.archiveCancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              variant='destructive'
              loading={archiveMutation.isPending}
              onClick={async () => {
                if (!archiveTarget) return;
                const ok = await handleArchive(
                  archiveTarget.id,
                  archiveTarget.name,
                );
                if (ok) setArchiveTarget(null);
              }}
            >
              {t('detail.archiveConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
